<template>
  <dialog ref="dialog" class="trends-panel" :aria-labelledby="titleId" @cancel.prevent="$emit('close')" @keydown.esc.stop.prevent="$emit('close')" @click="closeBackdrop">
    <header class="panel-heading">
      <div><h2 :id="titleId">Trends</h2><p>Explore saved dashboard history.</p></div>
      <button type="button" class="close-panel" aria-label="Close trends" autofocus @click="$emit('close')"><i class="bi-x-lg" aria-hidden="true" /></button>
    </header>
    <div class="trend-selectors">
      <label>View
        <select v-model="metric">
          <option value="listingTrends">Listing trends</option>
          <option value="platformListingCount">Active listings by platform</option>
          <option value="complianceTrends">Compliance trends</option>
        </select>
      </label>
    </div>
    <DashboardListingTrends v-if="metric === 'listingTrends'" v-model:period="period" :snapshot="snapshot" :rows="rows" :loading="loading" :error="error" @retry="$emit('retry')" />
    <DashboardTrendStack v-else v-model:period="period" :title="groupTitle" :snapshot="snapshot" :rows="trendRows" :metrics="metrics" :definition="definition"
      :loading="loading" :error="error" @retry="$emit('retry')" />
    <p class="history-note">Only snapshots using the current counting rules are shown. Gray dashed lines bridge missing or incompatible days; they are visual guides, not measured values.</p>
  </dialog>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref } from 'vue';
import { dashboardPlatformCounts, normalizeDashboardPlatform } from '@strenforcement/domain/dashboardListings';
import { calculatePropertyCompliance, dashboardLicensedPropertyCounts, normalizeComplianceOptions } from '@strenforcement/domain/dashboardCompliance';
import { CURRENT_DASHBOARD_SNAPSHOT_VERSION, dashboardSnapshotDate } from '@strenforcement/domain/dashboardSnapshot';
import { getListingSourceInfo } from '@strenforcement/domain/listing';
import { historyComplianceRate } from './dashboardTrendData';
import DashboardTrendStack from './DashboardTrendStack.vue';
import DashboardListingTrends from './DashboardListingTrends.vue';

const props = defineProps({ snapshot: { type: Object, required: true }, rows: { type: Array, default: () => [] }, loading: Boolean, error: String,
  initialMetric: { type: String, default: 'listingTrends' }, complianceOptions: Object });
const emit = defineEmits(['close', 'retry']);
const metric = ref(props.initialMetric === 'verificationRate' ? 'listingTrends' : props.initialMetric);
const period = ref(7);
const dialog = ref(null);
const titleId = `dashboard-trends-${getCurrentInstance().uid}`;
const currentPlatforms = computed(() => dashboardPlatformCounts(props.snapshot.inventory));
const historicalPlatforms = row => dashboardPlatformCounts({ activeListings: row.activeListings, bySource: row.platformListings });
const platforms = computed(() => {
  const sources = new Set(['airbnb', 'vrbo', 'booking', ...(currentPlatforms.value || []).map(row => row.source)]);
  const end = dashboardSnapshotDate(props.snapshot.generatedAt);
  for (const row of props.rows) {
    if (row.snapshotVersion !== CURRENT_DASHBOARD_SNAPSHOT_VERSION || !end || row.date > end || Date.parse(row.generatedAt) > Date.parse(props.snapshot.generatedAt)) continue;
    for (const item of historicalPlatforms(row) || []) sources.add(item.source);
  }
  return [...sources].map(source => ({ source, label: getListingSourceInfo(source).name }));
});
const countForPlatform = (rows, source) => rows ? rows.find(row => normalizeDashboardPlatform(row.source) === source)?.count ?? 0 : null;
const platformColors = { airbnb: '#d4606d', vrbo: '#4267b2', booking: '#7e62a3' };
const options = computed(() => normalizeComplianceOptions(props.complianceOptions));
const licensedProperties = computed(() => dashboardLicensedPropertyCounts(props.snapshot.propertyCompliance));
const groupTitle = computed(() => metric.value === 'complianceTrends' ? 'Compliance trends' : 'Active listings by platform');
const metrics = computed(() => metric.value === 'complianceTrends' ? [
  { metric: 'complianceRate', label: 'Compliance rate', value: calculatePropertyCompliance(props.snapshot.propertyCompliance, options.value)?.rate ?? null,
    color: '#2a9d5c', definition: 'Uses the compliance card’s selected calculation options.' },
  { metric: 'openViolations', label: 'Open violations', value: props.snapshot.violations?.openCount ?? null,
    color: '#d44b4b', definition: 'Recorded properties in violation, including deferred properties.' },
  { metric: 'activeLicensedProperties', label: 'Active licenses', value: licensedProperties.value?.active ?? null,
    color: '#3b7dd8', definition: 'Unique properties whose most relevant license is active, listed or not.' },
  { metric: 'pendingLicensedProperties', label: 'Pending licenses', value: licensedProperties.value?.pending ?? null,
    color: '#b8860b', definition: 'Unique properties whose most relevant license is pending, listed or not.' }
] : platforms.value.map(item => ({ metric: `platform:${item.source}`, label: item.label,
  value: countForPlatform(currentPlatforms.value, item.source), color: platformColors[item.source] || '#546875', definition: 'Active listings, including deferred listings.' })));
const policyLabel = value => value === 'noncompliant' ? 'non-compliant' : value;
const definition = computed(() => metric.value === 'complianceTrends'
  ? `Compliance uses the card’s settings: deferred ${policyLabel(options.value.deferred)}; licensed but not listed ${policyLabel(options.value.unlisted)}; pending ${policyLabel(options.value.pending)}. Counts are unique properties. License counts use each property’s most relevant license, listed or not. Open violations include deferrals. These counts do not change with calculation settings.`
  : 'Active listings by platform, including deferred listings. Changes are net listing counts, not newly discovered listings.');
const trendRows = computed(() => props.rows.map(row => {
  if (metric.value === 'complianceTrends') {
    const licensed = dashboardLicensedPropertyCounts(row.complianceBasis);
    return { ...row, complianceRate: historyComplianceRate(row, options.value),
      activeLicensedProperties: row.activeLicensedProperties ?? licensed?.active ?? null,
      pendingLicensedProperties: row.pendingLicensedProperties ?? licensed?.pending ?? null };
  }
  const counts = historicalPlatforms(row);
  return { ...row, ...Object.fromEntries(platforms.value.map(item => [`platform:${item.source}`, countForPlatform(counts, item.source)])) };
}));

let previousFocus;
let previousOverflow;
onMounted(() => {
  previousFocus = document.activeElement;
  previousOverflow = document.body.style.overflow;
  dialog.value.showModal();
  document.body.style.overflow = 'hidden';
});
onBeforeUnmount(() => {
  dialog.value?.close();
  document.body.style.overflow = previousOverflow;
  if (previousFocus?.isConnected) previousFocus.focus();
});
function closeBackdrop(event) {
  if (event.target !== dialog.value) return;
  const bounds = dialog.value.getBoundingClientRect();
  if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) emit('close');
}
</script>

<style scoped>
.trends-panel { box-sizing: border-box; position: fixed; inset: 0 0 0 auto; margin: 0; width: min(580px, 100vw); max-width: 100vw; height: 100dvh; max-height: 100dvh; padding: 28px; border: 0; border-left: 1px solid var(--border); background: var(--surface); color: var(--text-primary); overflow-y: auto; box-shadow: -12px 0 40px #0001; }
.trends-panel::backdrop { background: #17233355; }
.panel-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 28px; }
h2 { margin: 0 0 6px; font-size: 20px; font-weight: 600; }
p { color: var(--text-secondary); font-size: 12px; line-height: 1.6; margin: 0; }
.close-panel { flex: 0 0 32px; height: 32px; border: 1px solid var(--border); border-radius: 6px; background: transparent; color: var(--text-secondary); }
.trend-selectors { display: grid; gap: 16px; margin-bottom: 28px; }
label { display: grid; gap: 6px; color: var(--text-secondary); font-size: 12px; }
select { width: 100%; padding: 9px 10px; border: 1px solid var(--border); border-radius: 6px; color: var(--text-primary); background: var(--surface); font-size: 13px; }
button:focus-visible, select:focus-visible { outline: 2px solid var(--blue); outline-offset: 2px; }
.history-note { margin-top: 24px; padding-top: 16px; border-top: 1px solid var(--border); }
@media (max-width: 600px) { .trends-panel { width: 100vw; border: 0; padding: 20px; } select { font-size: 16px; } .close-panel { flex-basis: 44px; height: 44px; } }
</style>
